import { createContext, useState, useEffect } from "react";
import { ICompany, IStaffUser } from "../types";
import authService from "../services/auth.service";

export interface AuthContextType {
  user: ICompany | IStaffUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: any) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<ICompany | IStaffUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // ✅ FIX: Start with true during initialization

  // Rehydrate auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        
        if (token && userStr) {
          try {
            const parsedUser = JSON.parse(userStr);
            setUser(parsedUser);
            setIsAuthenticated(true);
            // ...existing code...
          } catch (error) {
            console.error('[AuthContext] Failed to parse user data:', error);
            // Clear corrupted data
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("userType");
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          // ...existing code...
        }
      } catch (error) {
        console.error('[AuthContext] Initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        // ✅ FIX: Always set loading to false after initialization completes
        setLoading(false);
        // ...existing code...
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authService.login({ email, password });
      if (!response.success || !response.data) {
        throw new Error(response.message || "Login failed");
      }

      const currentUser = response.data.company || response.data.user || null;
      const userType = response.data.company ? "company" : "staff";
      setUser(currentUser);
      setIsAuthenticated(true);
      
      // Persist all auth data for refreshes
      localStorage.setItem("user", JSON.stringify(currentUser));
      localStorage.setItem("userType", userType);
      if (response.data.token) localStorage.setItem("token", response.data.token);
      if (response.data.refreshToken) localStorage.setItem("refreshToken", response.data.refreshToken);
      
      // ...existing code...
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear localStorage on logout
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    
    // ...existing code...
  };

  const register = async (data: any) => {
    setLoading(true);
    try {
      const response = await authService.register(data);
      if (!response.success) {
        throw new Error(response.message || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};